import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TYPES_EVENEMENT } from '@artosera/shared';
import { Button, Checkbox, Input, Select, Textarea } from '@artosera/ui';
import { useExposition, useExpositions } from '../../api/expositions.js';
import { useLieux } from '../../api/lieux.js';
import type { CorpsEvenement, EvenementPresente } from '../../api/evenements.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './Evenements.module.css';

function dateLocale(date?: string | null) {
  if (!date) return '';
  const instant = new Date(date);
  return new Date(+instant - instant.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
export function FormulaireEvenement({
  evenement,
  onEnregistrer,
  occupe,
  lectureSeule = false,
}: {
  evenement?: EvenementPresente;
  onEnregistrer: (corps: CorpsEvenement) => void;
  occupe: boolean;
  lectureSeule?: boolean;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [type, setType] = useState<CorpsEvenement['type']>(evenement?.type ?? 'vernissage');
  const [expositionId, setExpositionId] = useState(evenement?.expositionId ?? '');
  const [titre, setTitre] = useState(evenement?.titre.fr ?? '');
  const [titreEn, setTitreEn] = useState(evenement?.titre.en ?? '');
  const [debut, setDebut] = useState(dateLocale(evenement?.debut));
  const [fin, setFin] = useState(dateLocale(evenement?.fin));
  const [lieuId, setLieuId] = useState(evenement?.lieuId ?? '');
  const [jauge, setJauge] = useState(evenement?.jauge?.toString() ?? '');
  const [places, setPlaces] = useState(evenement?.placesParInscriptionMax ?? 2);
  const [attente, setAttente] = useState(evenement?.listeAttente ?? true);
  const [acces, setAcces] = useState(evenement?.acces ?? 'public');
  const [description, setDescription] = useState(evenement?.description?.fr ?? '');
  const [descriptionEn, setDescriptionEn] = useState(evenement?.description?.en ?? '');
  const [confirmation, setConfirmation] = useState(evenement?.messageConfirmation?.fr ?? '');
  const [confirmationEn, setConfirmationEn] = useState(evenement?.messageConfirmation?.en ?? '');
  const [ouverture, setOuverture] = useState(dateLocale(evenement?.inscriptionsDesLe));
  const [fermeture, setFermeture] = useState(dateLocale(evenement?.inscriptionsJusquAu));
  const [rappel, setRappel] = useState(evenement?.rappel ?? '24h');
  const expositions = useExpositions(
    galerieSlug,
    { parPage: 200 },
    { enabled: galerieALaction(galerie, 'exhibitions:read') },
  );
  const exposition = useExposition(galerieSlug, expositionId || null);
  const lieux = useLieux(galerieSlug);
  const vernissage = exposition.data?.exposition.vernissage;
  const fige = !!evenement && evenement.placesConfirmees + evenement.placesEnAttente > 0;
  const valide =
    (type === 'vernissage'
      ? !!expositionId && !!exposition.data
      : !!titre.trim() && !!debut && (!fin || fin >= debut)) &&
    (!jauge || (Number.isInteger(Number(jauge)) && Number(jauge) >= 1)) &&
    Number.isInteger(places) &&
    places >= 1 &&
    places <= 10;
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valide || lectureSeule) return;
        const corps: CorpsEvenement = {
          type,
          titre: { fr: titre, en: titreEn },
          expositionId: expositionId || null,
          lieuId: lieuId || null,
          jauge: jauge ? Number(jauge) : null,
          placesParInscriptionMax: places,
          listeAttente: attente,
          acces,
        };
        if (type !== 'vernissage') {
          corps.debut = new Date(debut).toISOString();
          corps.fin = fin ? new Date(fin).toISOString() : null;
        }
        if (evenement) {
          corps.description = { fr: description, en: descriptionEn };
          corps.messageConfirmation = { fr: confirmation, en: confirmationEn };
          corps.inscriptionsDesLe = ouverture ? new Date(ouverture).toISOString() : null;
          corps.inscriptionsJusquAu = fermeture ? new Date(fermeture).toISOString() : null;
          corps.rappel = rappel;
        }
        onEnregistrer(corps);
      }}
    >
      <Field htmlFor="evt-type" label={t('evenements.type')}>
        <Select
          id="evt-type"
          value={type}
          disabled={lectureSeule || fige}
          onValueChange={(v) => setType(v as CorpsEvenement['type'])}
          options={TYPES_EVENEMENT.map((v) => ({ value: v, label: t(`evenements.types.${v}`) }))}
        />
      </Field>
      <Field htmlFor="evt-exposition" label={t('evenements.exposition')}>
        <Select
          id="evt-exposition"
          value={expositionId}
          disabled={lectureSeule || fige}
          onValueChange={setExpositionId}
          options={[
            { value: '', label: t('evenements.aucune') },
            ...(expositions.data?.expositions.map((x) => ({ value: x.id, label: x.titre.fr })) ??
              []),
          ]}
        />
      </Field>
      {expositions.isError || exposition.isError || lieux.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : null}
      {type === 'vernissage' ? (
        <>
          <p>{t('evenements.datesExposition')}</p>
          <Field htmlFor="evt-date" label={t('evenements.date')}>
            <Input id="evt-date" readOnly value={vernissage?.date ?? ''} />
          </Field>
          <Field htmlFor="evt-debut" label={t('evenements.debut')}>
            <Input id="evt-debut" readOnly value={vernissage?.debut ?? ''} />
          </Field>
          <Field htmlFor="evt-fin" label={t('evenements.fin')}>
            <Input id="evt-fin" readOnly value={vernissage?.fin ?? ''} />
          </Field>
        </>
      ) : (
        <>
          <Field htmlFor="evt-debut" label={t('evenements.debut')}>
            <Input
              id="evt-debut"
              type="datetime-local"
              required
              value={debut}
              disabled={lectureSeule}
              onChange={(e) => setDebut(e.target.value)}
            />
          </Field>
          <Field htmlFor="evt-fin" label={t('evenements.fin')}>
            <Input
              id="evt-fin"
              type="datetime-local"
              min={debut}
              value={fin}
              disabled={lectureSeule}
              onChange={(e) => setFin(e.target.value)}
            />
          </Field>
        </>
      )}
      <Field htmlFor="evt-titre" label={t('evenements.titreChamp')}>
        <Input
          id="evt-titre"
          required={type !== 'vernissage'}
          maxLength={10000}
          value={titre}
          disabled={lectureSeule}
          onChange={(e) => setTitre(e.target.value)}
        />
      </Field>
      <Field htmlFor="evt-titre-en" label={t('evenements.titreEn')}>
        <Input
          id="evt-titre-en"
          maxLength={10000}
          value={titreEn}
          disabled={lectureSeule}
          onChange={(e) => setTitreEn(e.target.value)}
        />
      </Field>
      <Field htmlFor="evt-lieu" label={t('evenements.lieu')}>
        <Select
          id="evt-lieu"
          value={lieuId}
          disabled={lectureSeule}
          onValueChange={setLieuId}
          options={[
            { value: '', label: t('evenements.galerie') },
            ...(lieux.data?.map((l) => ({ value: l.id, label: l.nom })) ?? []),
          ]}
        />
      </Field>
      <Field htmlFor="evt-jauge" label={t('evenements.jauge')}>
        <Input
          id="evt-jauge"
          type="number"
          min={1}
          step={1}
          value={jauge}
          disabled={lectureSeule}
          onChange={(e) => setJauge(e.target.value)}
        />
      </Field>
      <Field htmlFor="evt-places" label={t('evenements.placesMax')}>
        <Input
          id="evt-places"
          type="number"
          min={1}
          max={10}
          step={1}
          value={places}
          disabled={lectureSeule}
          onChange={(e) => setPlaces(Number(e.target.value))}
        />
      </Field>
      <Checkbox
        label={t('evenements.listeAttente')}
        checked={attente}
        disabled={lectureSeule}
        onCheckedChange={(v) => setAttente(v === true)}
      />
      <Field htmlFor="evt-acces" label={t('evenements.acces')}>
        <Select
          id="evt-acces"
          value={acces}
          disabled={lectureSeule}
          onValueChange={(v) => setAcces(v as typeof acces)}
          options={['public', 'sur_lien'].map((v) => ({
            value: v,
            label: t(`evenements.accesTypes.${v}`),
          }))}
        />
      </Field>
      {evenement ? (
        <>
          {(
            [
              ['description', description, setDescription],
              ['descriptionEn', descriptionEn, setDescriptionEn],
              ['confirmation', confirmation, setConfirmation],
              ['confirmationEn', confirmationEn, setConfirmationEn],
            ] as const
          ).map(([cle, valeur, changer]) => (
            <Field key={cle} htmlFor={`evt-${cle}`} label={t(`evenements.${cle}`)}>
              <Textarea
                id={`evt-${cle}`}
                maxLength={10000}
                value={valeur}
                disabled={lectureSeule}
                onChange={(e) => changer(e.target.value)}
              />
            </Field>
          ))}
          <Field htmlFor="evt-ouverture" label={t('evenements.ouverture')}>
            <Input
              id="evt-ouverture"
              type="datetime-local"
              value={ouverture}
              disabled={lectureSeule}
              onChange={(e) => setOuverture(e.target.value)}
            />
          </Field>
          <Field htmlFor="evt-fermeture" label={t('evenements.fermeture')}>
            <Input
              id="evt-fermeture"
              type="datetime-local"
              min={ouverture}
              value={fermeture}
              disabled={lectureSeule}
              onChange={(e) => setFermeture(e.target.value)}
            />
          </Field>
          <Field htmlFor="evt-rappel" label={t('evenements.rappel')}>
            <Select
              id="evt-rappel"
              value={rappel}
              disabled={lectureSeule}
              onValueChange={(v) => setRappel(v as typeof rappel)}
              options={['aucun', '24h', '48h'].map((v) => ({
                value: v,
                label: t(`evenements.rappels.${v}`),
              }))}
            />
          </Field>
        </>
      ) : null}
      {!lectureSeule ? (
        <Button type="submit" disabled={!valide} loading={occupe}>
          {t('commun:actions.enregistrer')}
        </Button>
      ) : null}
    </form>
  );
}
